import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import * as sketch from './sketch.js'
import * as planes from './planes.js'

const MODEL_URL = '/assets/ruin.glb'

let _state = null

export function initScene() {
  const scene = new THREE.Scene()

  const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.01, 20)

  // Drop any leftover canvas from a previous session so we don't pile up
  // hidden / dead WebGL contexts in the DOM.
  for (const old of document.querySelectorAll('canvas[data-ar-cheology]')) {
    old.remove()
  }

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
  renderer.setPixelRatio(window.devicePixelRatio)
  renderer.setSize(window.innerWidth, window.innerHeight)
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.xr.enabled = true
  renderer.domElement.dataset.arCheology = 'true'
  document.body.appendChild(renderer.domElement)
  renderer.domElement.style.position = 'fixed'
  renderer.domElement.style.inset = '0'
  renderer.domElement.style.zIndex = '1'

  const hemi = new THREE.HemisphereLight(0xffffff, 0x444444, 1.0)
  scene.add(hemi)

  const dir = new THREE.DirectionalLight(0xffffff, 0.8)
  dir.position.set(1, 3, 2)
  scene.add(dir)

  const reticle = buildReticle()
  reticle.visible = false
  scene.add(reticle)

  const modelGroup = new THREE.Group()
  modelGroup.visible = false
  scene.add(modelGroup)

  loadModel()
    .then((model) => {
      modelGroup.add(model)
      console.info('[scene] ruin model loaded')
    })
    .catch((err) => {
      console.warn('[scene] falling back to placeholder model:', err.message)
      modelGroup.add(buildPlaceholder())
    })

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight
    camera.updateProjectionMatrix()
    renderer.setSize(window.innerWidth, window.innerHeight)
    // LineMaterial uses pixel-space linewidth, so it needs to know the
    // viewport. No-op until sketch / planes are initialized.
    sketch.onResize(window.innerWidth, window.innerHeight)
    planes.onResize(window.innerWidth, window.innerHeight)
  })

  _state = { scene, camera, renderer, reticle, modelGroup }
  return _state
}

export function getSceneState() {
  if (!_state) throw new Error('scene not initialized')
  return _state
}

function buildReticle() {
  const group = new THREE.Group()

  const ringGeo = new THREE.RingGeometry(0.12, 0.15, 48).rotateX(-Math.PI / 2)
  const ringMat = new THREE.MeshBasicMaterial({
    color: 0xe6b65a,
    transparent: true,
    opacity: 0.95,
    depthTest: false
  })
  const ring = new THREE.Mesh(ringGeo, ringMat)
  ring.renderOrder = 999
  group.add(ring)

  const dotGeo = new THREE.CircleGeometry(0.02, 24).rotateX(-Math.PI / 2)
  const dotMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.95,
    depthTest: false
  })
  const dot = new THREE.Mesh(dotGeo, dotMat)
  dot.renderOrder = 1000
  group.add(dot)

  group.matrixAutoUpdate = false
  return group
}

function buildPlaceholder() {
  const group = new THREE.Group()

  // Use MeshBasicMaterial (unlit) so visibility doesn't depend on AR light
  // estimation or tone-mapping luck. Bright saturated colors so the
  // placement is unmistakeable on a phone screen in any lighting.
  const stone = new THREE.MeshBasicMaterial({ color: 0xc89b3c })
  const accent = new THREE.MeshBasicMaterial({ color: 0xff4d6d })

  // Stack of "stones" approximating a small ruin pile.
  const dims = [
    { size: [0.6, 0.05, 0.6], pos: [0, 0.025, 0], mat: stone },
    { size: [0.5, 0.3, 0.5], pos: [0, 0.2, 0], mat: stone },
    { size: [0.4, 0.25, 0.4], pos: [0.05, 0.475, 0.02], mat: stone },
    { size: [0.3, 0.2, 0.3], pos: [-0.05, 0.7, -0.04], mat: stone },
    { size: [0.2, 0.15, 0.2], pos: [0.02, 0.875, 0.05], mat: stone }
  ]
  for (const { size, pos, mat } of dims) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(...size), mat)
    m.position.set(...pos)
    group.add(m)
  }

  // Tall pink rod so the placement is unmistakable even at distance.
  const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.8, 16), accent)
  rod.position.set(0, 1.35, 0)
  group.add(rod)

  // Sphere on top.
  const top = new THREE.Mesh(new THREE.SphereGeometry(0.07, 24, 16), accent)
  top.position.set(0, 1.78, 0)
  group.add(top)

  // Full size — was 0.5 which made the stack ~50cm tall and easy to miss.
  group.scale.setScalar(1.0)
  return group
}

async function loadModel() {
  // Probe with a real GET so we can check the Content-Type. CloudFront's
  // SPA fallback rewrites 404s to a 200 with index.html, so a HEAD/200 is
  // not enough to know the GLB exists — we have to look at the body type.
  const probe = await fetch(MODEL_URL).catch(() => null)
  if (!probe || !probe.ok) {
    throw new Error(`ruin.glb not reachable at ${MODEL_URL}`)
  }
  const ct = (probe.headers.get('content-type') || '').toLowerCase()
  if (!ct.includes('gltf') && !ct.includes('octet-stream') && !ct.includes('binary')) {
    throw new Error(`ruin.glb missing — got Content-Type "${ct}" instead of model/gltf-binary`)
  }

  const loader = new GLTFLoader()
  // DRACO support — the export step uses Draco compression.
  const draco = new DRACOLoader()
  draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/')
  loader.setDRACOLoader(draco)

  const buf = await probe.arrayBuffer()
  const gltf = await loader.parseAsync(buf, '')
  // Trust the model's authored scale — the export pipeline (Blender) is
  // responsible for sizing the ruin to its real-world meters.
  return gltf.scene
}
